import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Button } from "primereact/button";
import { InputText as PrimeInputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Checkbox } from "primereact/checkbox";
import { useNavigate, useParams } from "react-router-dom";
import { Card } from "primereact/card";
import apiFunctions from "../../services/api-service";
import useToast from "../../components/common/useToast";
import { AxiosError } from "axios";
import { Select, Spin } from "antd";
import { errorHandler } from "../../utils/HelperFunctions";
import { ArrowLeftIcon, TrashIcon } from "@heroicons/react/24/outline";

const { Option } = Select;

interface FormValues {
    userid: string;
    businessid: string;
    name: string;
    nameGujarati: string;
    category: string;
    price: string;
    description: string;
    isactive: boolean;
}

const ProductAdd: React.FC = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { showToast }: any = useToast();

    const [loading, setLoading] = useState(false);

    const [userOptions, setUserOptions] = useState<any[]>([]);
    const [userSearchValue, setUserSearchValue] = useState("");
    const [fetchingUsers, setFetchingUsers] = useState(false);
    const [firstUser, setFirstUser] = useState(0);
    const [totalUsersCount, setTotalUsersCount] = useState(0);
    const fetchingUsersRef = useRef(false);
    const ownerOptionRef = useRef<any>(null);
    const rows = 10;

    const [businessOptions, setBusinessOptions] = useState<any[]>([]);
    const [businessSearchValue, setBusinessSearchValue] = useState("");
    const [fetchingBusiness, setFetchingBusiness] = useState(false);
    const fetchingBusinessRef = useRef(false);
    const businessOptionRef = useRef<any>(null);

    const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
    const [fetchingCategories, setFetchingCategories] = useState(false);

    const [imageFiles, setImageFiles] = useState<File[]>([]);
    const [imagePreviews, setImagePreviews] = useState<string[]>([]);
    const [existingImages, setExistingImages] = useState<{ filename: string; url: string }[]>([]);
    const imageInputRef = useRef<HTMLInputElement | null>(null);

    const dedupeUsers = (list: any[]) =>
        list.filter((u, i, arr) => arr.findIndex(x => (x.id || x._id) === (u.id || u._id)) === i);

    const formik = useFormik<FormValues>({
        initialValues: {
            userid: "",
            businessid: "",
            name: "",
            nameGujarati: "",
            category: "",
            price: "",
            description: "",
            isactive: true,
        },
        validationSchema: Yup.object({
            userid: Yup.string().required("Owner is required"),
            name: Yup.string().required("Product name is required"),
            category: Yup.string().required("Category is required"),
            price: Yup.number().typeError("Price must be a number").required("Price is required"),
        }),
        onSubmit: async (values) => {
            try {
                setLoading(true);

                const formData = new FormData();
                Object.entries(values).forEach(([key, value]) => {
                    formData.append(key, String(value));
                });
                imageFiles.forEach(f => formData.append("Images", f));

                if (id) {
                    formData.append("existingImages", JSON.stringify(existingImages.map(img => img.filename)));
                    const response = await apiFunctions.put(`/product/admin/update/${id}`, formData, undefined, true);
                    if (response?.data?.success) {
                        showToast({ severity: "success", summary: "Updated", detail: "Product updated successfully", life: 3000 });
                        navigate("/admin/products");
                    } else {
                        showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Update failed", life: 3000 });
                    }
                } else {
                    const response = await apiFunctions.post("/product/admin/create", formData, null, true);
                    if (response?.data?.success) {
                        showToast({ severity: "success", summary: "Created", detail: "Product created successfully", life: 3000 });
                        navigate("/admin/products");
                    } else {
                        showToast({ severity: "error", summary: "Error", detail: response?.data?.message || "Create failed", life: 3000 });
                    }
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            } finally {
                setLoading(false);
            }
        },
    });

    const fetchUsers = async (reset = false) => {
        if (fetchingUsersRef.current) return;
        fetchingUsersRef.current = true;
        setFetchingUsers(true);
        const skip = reset ? 0 : firstUser;
        try {
            let url = `/user/dropdownlist/${skip}/${rows}`;
            if (userSearchValue && userSearchValue.length > 1) url += `/${userSearchValue}`;
            const response = await apiFunctions.get(url);
            const newItems = response?.data?.data || [];
            const total = response?.data?.totalRecords || 0;
            if (reset) {
                setUserOptions(dedupeUsers([
                    ...(ownerOptionRef.current ? [ownerOptionRef.current] : []),
                    ...newItems
                ]));
                setFirstUser(rows);
            } else {
                setUserOptions(prev => dedupeUsers([...prev, ...newItems]));
                setFirstUser(prev => prev + rows);
            }
            setTotalUsersCount(total);
        } finally {
            fetchingUsersRef.current = false;
            setFetchingUsers(false);
        }
    };

    useEffect(() => {
        setFirstUser(0);
        fetchUsers(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userSearchValue]);

    const handleUserScroll = (e: any) => {
        const { scrollTop, offsetHeight, scrollHeight } = e.target;
        if (scrollTop + offsetHeight >= scrollHeight - 10 && userOptions.length < totalUsersCount && !fetchingUsers) {
            fetchUsers();
        }
    };

    // businesses of the selected owner only
    const fetchBusinesses = async () => {
        if (fetchingBusinessRef.current) return;
        if (!formik.values.userid) {
            setBusinessOptions(businessOptionRef.current ? [businessOptionRef.current] : []);
            return;
        }
        fetchingBusinessRef.current = true;
        setFetchingBusiness(true);
        try {
            const response = await apiFunctions.get("/business/admin/list", {
                params: {
                    page: 1,
                    limit: 50,
                    search: businessSearchValue || "",
                    userid: formik.values.userid,
                }
            });
            const items = response?.data?.data || [];
            setBusinessOptions(
                businessOptionRef.current && !items.some((b: any) => b._id === businessOptionRef.current._id)
                    ? [businessOptionRef.current, ...items]
                    : items
            );
        } finally {
            fetchingBusinessRef.current = false;
            setFetchingBusiness(false);
        }
    };

    useEffect(() => {
        fetchBusinesses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [businessSearchValue, formik.values.userid]);

    // product categories — fetched once
    useEffect(() => {
        (async () => {
            try {
                setFetchingCategories(true);
                const response = await apiFunctions.get("/productmobile/mobile/productcategoryinside");
                if (response?.data?.success) {
                    setCategoryOptions(response.data.data || []);
                }
            } catch (err) {
                console.error("Failed to load categories:", err);
            } finally {
                setFetchingCategories(false);
            }
        })();
    }, []);

    // Edit mode: load existing product
    useEffect(() => {
        if (!id) return;
        (async () => {
            try {
                const response = await apiFunctions.get(`/product/admin/${id}`);
                if (response?.data?.success) {
                    const p = response.data.data;
                    formik.setValues({
                        userid: p.userid || "",
                        businessid: p.businessid || "",
                        name: p.name || "",
                        nameGujarati: p.nameGujarati || "",
                        category: p.category || "",
                        price: p.price != null ? String(p.price) : "",
                        description: p.description || "",
                        isactive: !!p.isactive,
                    });
                    setExistingImages((p.Images || []).map((f: string, i: number) => ({
                        filename: f,
                        url: (p.imageUrls || [])[i] || "",
                    })));
                    if (p.userid && p.ownerName) {
                        ownerOptionRef.current = { id: p.userid, name: p.ownerName };
                        setUserOptions(prev =>
                            prev.some(u => (u.id || u._id) === p.userid)
                                ? prev
                                : [ownerOptionRef.current, ...prev]
                        );
                    }
                    if (p.businessid && p.businessName) {
                        businessOptionRef.current = { _id: p.businessid, name: p.businessName };
                        setBusinessOptions(prev =>
                            prev.some(b => b._id === p.businessid)
                                ? prev
                                : [businessOptionRef.current, ...prev]
                        );
                    }
                }
            } catch (err) {
                errorHandler(err as AxiosError);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);
        setImageFiles(prev => [...prev, ...files]);
        setImagePreviews(prev => [...prev, ...files.map(f => URL.createObjectURL(f))]);
    };

    const removeNewImage = (index: number) => {
        setImageFiles(prev => prev.filter((_, i) => i !== index));
        setImagePreviews(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingImage = (index: number) => {
        setExistingImages(prev => prev.filter((_, i) => i !== index));
    };

    const fieldError = (name: keyof FormValues) =>
        formik.touched[name] && formik.errors[name] ? (
            <small className="p-error block mt-1">{formik.errors[name] as string}</small>
        ) : null;

    return (
        <div className="unlist-margin">
            <div className="back-button mb-2" onClick={() => navigate("/admin/products")}>
                <ArrowLeftIcon className="w-6 h-6 text-black-700" /><h3 className="m-0">Back</h3>
            </div>

            <Card title={id ? "Edit Product" : "Add Product"}>
                <form onSubmit={formik.handleSubmit}>
                    <div className="grid grid-cols-12 gap-4">

                        {/* Owner */}
                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Owner (User) *</label>
                            <Select
                                showSearch
                                allowClear
                                className="w-full h-[42px] mt-1"
                                placeholder="Select owner"
                                filterOption={false}
                                onSearch={(v) => setUserSearchValue(v)}
                                onPopupScroll={handleUserScroll}
                                notFoundContent={fetchingUsers ? <Spin size="small" /> : "No users"}
                                value={formik.values.userid || undefined}
                                onChange={(value) => {
                                    formik.setFieldValue("userid", value || "");
                                    // owner changed → their business list changes too
                                    formik.setFieldValue("businessid", "");
                                    businessOptionRef.current = null;
                                }}
                            >
                                {userOptions.map(user => (
                                    <Option key={user.id} value={user.id}>
                                        {user.name}{user.mobileno ? ` - ${user.mobileno}` : ""}
                                    </Option>
                                ))}
                            </Select>
                            {fieldError("userid")}
                        </div>

                        {/* Business (optional) */}
                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Business (Optional)</label>
                            <Select
                                showSearch
                                allowClear
                                className="w-full h-[42px] mt-1"
                                placeholder="Select business"
                                filterOption={false}
                                onSearch={(v) => setBusinessSearchValue(v)}
                                notFoundContent={fetchingBusiness ? <Spin size="small" /> : "No businesses"}
                                value={formik.values.businessid || undefined}
                                onChange={(value) => formik.setFieldValue("businessid", value || "")}
                            >
                                {businessOptions.map(b => (
                                    <Option key={b._id} value={b._id}>
                                        {b.name}
                                    </Option>
                                ))}
                            </Select>
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Name *</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.name}
                                onChange={(e) => formik.setFieldValue("name", e.target.value)} />
                            {fieldError("name")}
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Name (Gujarati)</label>
                            <PrimeInputText className="w-full mt-1" value={formik.values.nameGujarati}
                                onChange={(e) => formik.setFieldValue("nameGujarati", e.target.value)} />
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Category *</label>
                            <Select
                                showSearch
                                allowClear
                                className="w-full h-[42px] mt-1"
                                placeholder="Select category"
                                optionFilterProp="children"
                                notFoundContent={fetchingCategories ? <Spin size="small" /> : "No categories"}
                                value={formik.values.category || undefined}
                                onChange={(value) => formik.setFieldValue("category", value || "")}
                            >
                                {categoryOptions.map(cat => (
                                    <Option key={cat._id} value={cat._id}>
                                        {cat.name}
                                    </Option>
                                ))}
                            </Select>
                            {fieldError("category")}
                        </div>

                        <div className="col-span-12 lg:col-span-6">
                            <label className="form-label">Price (₹) *</label>
                            <PrimeInputText className="w-full mt-1" keyfilter="money" value={formik.values.price}
                                onChange={(e) => formik.setFieldValue("price", e.target.value)} />
                            {fieldError("price")}
                        </div>

                        <div className="col-span-12">
                            <label className="form-label">Description</label>
                            <InputTextarea className="w-full mt-1" rows={3} value={formik.values.description}
                                onChange={(e) => formik.setFieldValue("description", e.target.value)} />
                        </div>

                        {/* Images */}
                        <div className="col-span-12">
                            <label className="form-label">Product Images</label>
                            <div className="flex items-center gap-3 flex-wrap mt-1">
                                {existingImages.map((img, i) => (
                                    <div key={`ex-${i}`} style={{ position: "relative" }}>
                                        <img src={img.url} alt="product" style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb" }} />
                                        <Button type="button" icon={<TrashIcon style={{ width: "14px", height: "14px" }} />} rounded severity="danger"
                                            style={{ position: "absolute", top: "-8px", right: "-8px", width: "24px", height: "24px", padding: 0 }}
                                            onClick={() => removeExistingImage(i)} />
                                    </div>
                                ))}
                                {imagePreviews.map((p, i) => (
                                    <div key={`new-${i}`} style={{ position: "relative" }}>
                                        <img src={p} alt="product" style={{ width: "80px", height: "80px", objectFit: "cover", borderRadius: "8px", border: "1px solid #e5e7eb" }} />
                                        <Button type="button" icon={<TrashIcon style={{ width: "14px", height: "14px" }} />} rounded severity="danger"
                                            style={{ position: "absolute", top: "-8px", right: "-8px", width: "24px", height: "24px", padding: 0 }}
                                            onClick={() => removeNewImage(i)} />
                                    </div>
                                ))}
                                <Button type="button" label="Add Images" outlined onClick={() => imageInputRef.current?.click()} />
                                <input ref={imageInputRef} type="file" accept="image/*" multiple hidden onChange={handleImageChange} />
                            </div>
                        </div>

                        {/* Active */}
                        <div className="col-span-12 flex items-center gap-2">
                            <Checkbox inputId="isactive" checked={formik.values.isactive}
                                onChange={(e) => formik.setFieldValue("isactive", e.checked)} />
                            <label htmlFor="isactive" className="form-label m-0 cursor-pointer">Active</label>
                        </div>

                        <div className="col-span-12 flex justify-end gap-2 mt-2">
                            <Button type="button" label="Cancel" severity="secondary" onClick={() => navigate("/admin/products")} disabled={loading} />
                            <Button type="submit" label={id ? "Update" : "Create"} loading={loading} />
                        </div>

                    </div>
                </form>
            </Card>
        </div>
    );
};

export default ProductAdd;
